// API service for communicating with the FastAPI backend
// Enhanced with streaming support for real-time simulation updates

import axios from 'axios';
import { 
  SimulationRequest, 
  SimulationResults, 
  VariableInfo, 
  DatasetInfo, 
  PersonaPreview,
  StreamingUpdate,
  ModelConfig,
  TargetVariable
} from '../types/api';

// Use environment variable for API URL, fallback to localhost for development
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 600000, // 10 minutes timeout for AI simulations
});

export const apiService = {
  // Get dataset information
  async getDatasetInfo(targetVariable?: string): Promise<DatasetInfo> {
    const params = targetVariable ? { target_variable: targetVariable } : {};
    const response = await api.get<DatasetInfo>('/api/dataset/info', { params });
    return response.data;
  },

  // Get available variables
  async getVariables(): Promise<VariableInfo[]> {
    const response = await api.get<VariableInfo[]>('/api/variables');
    return response.data;
  },

  // Get available models
  async getModels(): Promise<{ models: ModelConfig[] }> {
    const response = await api.get<{ models: ModelConfig[] }>('/api/models');
    return response.data;
  },

  // Get available target variables
  async getTargetVariables(): Promise<{ target_variables: TargetVariable[] }> {
    const response = await api.get<{ target_variables: TargetVariable[] }>('/api/target-variables');
    return response.data;
  },

  // Run simulation (non-streaming)
  async runSimulation(request: SimulationRequest): Promise<SimulationResults> {
    const response = await api.post<SimulationResults>('/api/simulate', request);
    return response.data;
  },

  // Run simulation with streaming updates
  async runSimulationStreaming(
    request: SimulationRequest,
    onUpdate: (update: StreamingUpdate) => void,
    onError?: (error: Error) => void
  ): Promise<SimulationResults | null> {
    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/simulate/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let finalResults: SimulationResults | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6)) as StreamingUpdate;
              onUpdate(data);

              if (data.type === 'complete' && data.results) {
                finalResults = data.results;
              }
            } catch (parseError) {
              console.error('Error parsing SSE data:', parseError);
            }
          }
        }
      }

      return finalResults;
    } catch (error) {
      if (onError && error instanceof Error) {
        onError(error);
      }
      throw error;
    }
  },

  // Preview personas
  async previewPersonas(sampleSize: number = 3): Promise<{ previews: PersonaPreview[] }> {
    const response = await api.get<{ previews: PersonaPreview[] }>(`/api/simulation/preview/${sampleSize}`);
    return response.data;
  },

  // Health check
  async healthCheck(): Promise<{ message: string }> {
    const response = await api.get<{ message: string }>('/');
    return response.data;
  },
};
